import { CommonCMS } from "@lib/constants/contentful";
import ClickAndCollect from "@lib/constants/contentful/clickAndCollect";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { executeWithRetry } from "@lib/utils/executeWithRetry";
import { useErrorToast } from "@lib/utils/store-utility/storeUtility";
import { useLock, useUpdateLineItemSupplyChannel } from "@store/hooks";
import { Button } from "@ui/components/forms/button/Button";
import { Circle } from "@ui/components/layout/center/Circle";
import { Flex } from "@ui/components/layout/flex/Flex";
import { useRouter } from "next/router";
import { useState } from "react";
import type { StoreState } from "store";
import { useStore, useStoreApi } from "store";
import type { StoreApi } from "zustand";
import type { StoreData } from "../store-locator/controller";

type Props = {
    store?: StoreData;
    onClose?: () => void;
};

export const AvailabilityComponentCheckout = ({ store, onClose }: Props) => {
    const setStoreData = useStore((state) => state.setStoreData);
    const lock = useLock();
    const api: StoreApi<StoreState> = useStoreApi();

    const errorToaster = useErrorToast();
    const { locale } = useRouter();
    const { get: getMicrocopy, getMultiple } = useMicrocopy();
    const executeUpdateLineItemSupplyChannel = useUpdateLineItemSupplyChannel();
    const lineItems = api?.getState().data?.me?.cartInfo?.cart?.lineItems;
    const [loading, setLoading] = useState(false);

    const handleSetStore = lock(async () => {
        setLoading(true);
        const cart = api.getState().data.me.cartInfo.cart;
        const lastLineItem = cart?.lineItems.slice(-1);
        const { error } = await executeWithRetry(
            executeUpdateLineItemSupplyChannel,
            {
                cartId: cart.id,
                cartVersion: cart.version,
                storeKey: process.env.NEXT_PUBLIC_STORE_KEY,
                locale: locale,
                lineItemId: lastLineItem[0].id,
                supplyChannel: { id: store?.supplyChannel?.[0]?.id },
            },
            0,
            "Executing updateLineItemSupplyChannel"
        );

        if (error) {
            errorToaster({
                title: microcopies[CommonCMS.error.general.title],
            });
        }
        setStoreData(store);
        setLoading(false);
        onClose();
    });

    const allItemsAvailableInStore = lineItems?.every((lineItem) => {
        return lineItem?.variant?.availability?.channels?.results?.some((channel) => {
            return (
                channel.channel.id === store?.supplyChannel?.[0].id &&
                channel.availability.isOnStock
            );
        });
    });

    const unavailableItems = lineItems?.filter((lineItem) => {
        return !lineItem?.variant?.availability?.channels?.results?.some((channel) => {
            return (
                channel.channel.id === store?.supplyChannel?.[0].id &&
                channel.availability.isOnStock
            );
        });
    });

    const microcopies = (() => {
        return getMultiple(CommonCMS.page, [
            ClickAndCollect.availableSoon,
            ClickAndCollect.available,
            ClickAndCollect.selectStore,
            ClickAndCollect.changeStore,
            ClickAndCollect.availableSoon,
        ]);
    })();

    const unavailableInStoresText = getMicrocopy(
        CommonCMS.page,
        unavailableItems?.length === 1
            ? ClickAndCollect.availableSoon
            : ClickAndCollect.availableItems,
        {
            replacements: {
                storesWithoutStock: unavailableItems?.length,
            },
        }
    );
    const availabilityColor = allItemsAvailableInStore ? "green.200" : "yellow.200";
    let availabilityText: string;
    if (allItemsAvailableInStore) {
        availabilityText = microcopies[ClickAndCollect.available];
    } else {
        availabilityText = unavailableInStoresText;
    }

    return (
        <Flex
            justifyContent={"space-between"}
            color="black"
            fontWeight="bold"
            flexDirection={["column", "column", "row"]}
            gap={4}
        >
            <Flex gap={2} fontSize={"mobileBodyTextTiny"} alignItems={"center"}>
                <Circle size={2} bg={availabilityColor} />
                {availabilityText}
            </Flex>
            <Button
                zIndex={10}
                h={10}
                w={["100%", "100%", "auto"]}
                fontSize="mobileBodyTextNormal"
                onClick={handleSetStore}
                isLoading={loading}
            >
                <span>{microcopies[ClickAndCollect.selectStore]}</span>
            </Button>
        </Flex>
    );
};
